/**
 * ArchiveButton — Bouton réutilisable pour archiver un item (Batch #14.200).
 *
 * Props :
 *   - itemType : clé du type (invoice, work_order, lead, etc.) — doit matcher ITEM_TYPES backend
 *   - itemId : ID de l'item
 *   - itemLabel : (optionnel) label pour la confirmation
 *   - onArchived : callback après archivage réussi (pour rafraîchir la liste)
 *   - className : classes CSS supplémentaires
 *   - variant : "icon" (default, juste l'icône) | "button" (texte + icône)
 *
 * Affiche un bouton qui appelle POST /admin/archive/{type}/{id}.
 * Confirme avant action. Toast sur succès/erreur.
 */
import { useState } from "react";
import { Archive, Loader2 } from "lucide-react";
import { api } from "@/api";
import { toast } from "@/lib/toast";

export default function ArchiveButton({
  itemType,
  itemId,
  itemLabel = "cet élément",
  onArchived,
  className = "",
  variant = "icon",
  size = "sm",
}) {
  const [busy, setBusy] = useState(false);

  const handle = async () => {
    if (!window.confirm(`Archiver ${itemLabel} ?\n\nIl sera caché des listes actives. Vous pourrez le restaurer depuis Paramètres → Archives.`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/archive/${itemType}/${itemId}`);
      toast.success("Archivé · visible dans Paramètres → Archives");
      onArchived?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'archivage");
    } finally {
      setBusy(false);
    }
  };

  if (variant === "button") {
    return (
      <button
        onClick={handle}
        disabled={busy}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 text-xs disabled:opacity-50 ${className}`}
        data-testid={`archive-btn-${itemType}-${itemId}`}
        title="Archiver"
      >
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Archive className="w-3 h-3" />}
        Archiver
      </button>
    );
  }

  const iconSize = size === "xs" ? "w-3 h-3" : "w-3.5 h-3.5";
  return (
    <button
      onClick={handle}
      disabled={busy}
      className={`p-1.5 rounded hover:bg-amber-50 text-amber-600 disabled:opacity-40 ${className}`}
      data-testid={`archive-btn-${itemType}-${itemId}`}
      title="Archiver (cache des listes actives)"
    >
      {busy ? <Loader2 className={`${iconSize} animate-spin`} /> : <Archive className={iconSize} />}
    </button>
  );
}
